import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { apiClient } from '@services/api';
import BreadCrumbs from '@components/Breadcrumbs';
import Button from '@components/Button';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';

const CATEGORIES = [
  { value: 'bug',         label: 'Bug / Erro',  icon: 'bug_report',    desc: 'Algo não está funcionando' },
  { value: 'improvement', label: 'Melhoria',     icon: 'auto_fix_high', desc: 'Sugestão ou melhoria' },
  { value: 'question',    label: 'Dúvida',       icon: 'help_outline',  desc: 'Preciso de ajuda' },
  { value: 'logistics',   label: 'Logística',    icon: 'directions_bus',desc: 'Operação e logística' },
  { value: 'staging',     label: 'Sistema',      icon: 'layers',        desc: 'Problema no sistema' },
  { value: 'sales',       label: 'Vendas',       icon: 'point_of_sale', desc: 'Para o time de vendas' },
  { value: 'other',       label: 'Outro',        icon: 'more_horiz',    desc: 'Outro assunto' },
];

const PRIORITIES = [
  { value: 'low',    label: 'Baixa',   color: '#64748b' },
  { value: 'normal', label: 'Normal',  color: '#38bdf8' },
  { value: 'high',   label: 'Alta',    color: '#f59e0b' },
  { value: 'urgent', label: 'Urgente', color: '#ef4444' },
];

const ALLOWED_TYPES = ['image/jpeg','image/png','image/gif','image/webp',
                       'application/pdf','application/msword',
                       'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                       'text/plain'];

const Create = (props) => {
  const history = useHistory();
  const fileRef = useRef();

  const [subject,    setSubject]    = useState('');
  const [category,   setCategory]   = useState('');
  const [priority,   setPriority]   = useState('normal');
  const [body,       setBody]       = useState('');
  const [files,      setFiles]      = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors,     setErrors]     = useState({});
  const [toast,      setToast]      = useState(null);   // { text, type }
  const [sellers,    setSellers]    = useState([]);
  const [sellerId,   setSellerId]   = useState('');

  useEffect(() => { props.setShowMenu(false); }, []);

  // Carregar vendedores ativos quando categoria "sales" for selecionada
  useEffect(() => {
    if (category === 'sales' && sellers.length === 0) {
      apiClient.httpInstance.get('/users?seller=true&status=true&pageSize=500')
        .then(res => {
          const data = res.data?.data || res.data || [];
          setSellers(Array.isArray(data) ? data : []);
        });
    }
    if (category !== 'sales') setSellerId('');
  }, [category]);

  const breadcrumbs = [
    { title: 'Tickets', active: true, route: '/admin/tickets' },
    { title: 'Novo ticket' },
  ];

  const handleFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const valid  = chosen.filter(f => ALLOWED_TYPES.includes(f.type) && f.size <= 8 * 1024 * 1024);
    const merged = [...files, ...valid].slice(0, 5);
    setFiles(merged);
    if (valid.length < chosen.length) {
      setErrors(prev => ({ ...prev, files: 'Alguns arquivos foram ignorados (tipo não suportado ou > 8 MB).' }));
    } else {
      setErrors(prev => { const e = { ...prev }; delete e.files; return e; });
    }
    // reset input para permitir re-selecionar o mesmo arquivo
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSubmit = () => {
    const e = {};
    if (!subject.trim())  e.subject  = 'Informe o assunto.';
    if (!category)        e.category = 'Selecione uma categoria.';
    if (category === 'sales' && !sellerId) e.seller_id = 'Selecione o vendedor.';
    if (!body.trim())     e.body     = 'Descreva o problema ou solicitação.';
    if (Object.keys(e).length) { setErrors(e); return; }

    setSubmitting(true);

    const formData = new FormData();
    formData.append('subject',  subject);
    formData.append('category', category);
    formData.append('priority', priority);
    formData.append('body',     body);
    if (category === 'sales' && sellerId) formData.append('seller_id', sellerId);
    files.forEach((f, i) => formData.append(`files[${i}]`, f));

    apiClient.httpInstance
      .post('/tickets', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(res => {
        if (res.status === 201) {
          showToast(`Ticket #${res.data.data.id} aberto com sucesso!`);
          setTimeout(() => history.push(`/admin/tickets/${res.data.data.id}`), 1600);
        }
      })
      .catch(err => {
        if (err.response?.data?.errors) setErrors(err.response.data.errors);
        else showToast('Erro ao abrir o ticket. Tente novamente.', 'error');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <BreadCrumbs items={breadcrumbs} />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 24, zIndex: 99999,
          background: toast.type === 'error' ? '#7f1d1d' : '#134e35',
          border: `1px solid ${toast.type === 'error' ? '#ef444466' : '#22c55e66'}`,
          color: toast.type === 'error' ? '#fca5a5' : '#bbf7d0',
          padding: '12px 20px', borderRadius: 10,
          fontSize: 14, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 24px #0008',
          animation: 'fadeIn .2s ease',
        }}>
          <Icon style={{ fontSize: 18 }}>{toast.type === 'error' ? 'error_outline' : 'check_circle'}</Icon>
          {toast.text}
        </div>
      )}

      <div className="px-7 pb-10 max-w-2xl">
        <div className="mt-2 mb-6 text-2xl" style={{ color: props.theme.color }}>
          Novo ticket
        </div>

        {/* Assunto */}
        <Field label="Assunto" error={errors.subject}>
          <input
            type="text"
            className="w-full text-sm rounded-lg p-3"
            placeholder="Descreva brevemente o problema ou solicitação"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            maxLength={200}
            style={inputStyle}
          />
        </Field>

        {/* Categoria */}
        <Field label="Categoria" error={errors.category} className="mt-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                className="flex items-start gap-2 p-3 rounded-xl text-left transition-all"
                style={{
                  background: category === c.value ? props.theme.color + '22' : '#0f2340',
                  border: category === c.value
                    ? `2px solid ${props.theme.color}`
                    : '2px solid #1e3a5f',
                  cursor: 'pointer',
                }}
              >
                <Icon style={{ fontSize: 18, color: category === c.value ? props.theme.color : '#64748b', marginTop: 1 }}>
                  {c.icon}
                </Icon>
                <div>
                  <p className="text-xs font-medium" style={{ color: category === c.value ? '#fff' : '#94a3b8' }}>
                    {c.label}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>{c.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </Field>

        {/* Picker de vendedor — aparece só quando categoria = vendas */}
        {category === 'sales' && (
          <Field label="Vendedor" error={errors.seller_id} className="mt-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {sellers.length === 0 && (
                <p className="text-xs col-span-3" style={{ color: '#64748b' }}>
                  Carregando vendedores...
                </p>
              )}
              {sellers.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSellerId(String(s.user_id))}
                  className="flex items-center gap-2 p-3 rounded-xl text-left transition-all"
                  style={{
                    background: sellerId === String(s.user_id) ? props.theme.color + '22' : '#0f2340',
                    border: sellerId === String(s.user_id)
                      ? `2px solid ${props.theme.color}`
                      : '2px solid #1e3a5f',
                    cursor: 'pointer',
                  }}
                >
                  <Icon style={{ fontSize: 18, color: sellerId === String(s.user_id) ? props.theme.color : '#64748b' }}>
                    person
                  </Icon>
                  <p className="text-xs font-medium truncate" style={{ color: sellerId === String(s.user_id) ? '#fff' : '#94a3b8' }}>
                    {s.name}
                  </p>
                </button>
              ))}
            </div>
          </Field>
        )}

        {/* Prioridade */}
        <Field label="Prioridade" className="mt-5">
          <div className="flex gap-2">
            {PRIORITIES.map(p => (
              <button
                key={p.value}
                onClick={() => setPriority(p.value)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: priority === p.value ? p.color + '22' : '#0f2340',
                  border: priority === p.value ? `2px solid ${p.color}` : '2px solid #1e3a5f',
                  color: priority === p.value ? p.color : '#64748b',
                  cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </Field>

        {/* Descrição */}
        <Field label="Descrição" error={errors.body} className="mt-5">
          <textarea
            rows={6}
            className="w-full text-sm rounded-lg p-3 resize-none"
            placeholder="Detalhe o que está acontecendo, passos para reproduzir, o que você esperava..."
            value={body}
            onChange={e => setBody(e.target.value)}
            style={inputStyle}
          />
        </Field>

        {/* Anexos */}
        <Field label="Anexos (opcional)" error={errors.files} className="mt-5">
          <div
            className="rounded-xl p-4"
            style={{ background: '#0a1929', border: '1px dashed #1e3a5f' }}
          >
            {/* Lista de arquivos selecionados */}
            {files.length > 0 && (
              <div className="mb-3 space-y-2">
                {files.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs px-3 py-2 rounded-lg"
                    style={{ background: '#0f2340', color: '#94a3b8' }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon style={{ fontSize: 16, color: '#38bdf8' }}>
                        {f.type.startsWith('image/') ? 'image' : 'attach_file'}
                      </Icon>
                      <span className="truncate" style={{ maxWidth: 260 }}>{f.name}</span>
                      <span style={{ color: '#475569', flexShrink: 0 }}>
                        ({(f.size / 1024).toFixed(0)} KB)
                      </span>
                    </div>
                    <button
                      onClick={() => removeFile(i)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0 }}
                    >
                      <Icon style={{ fontSize: 16 }}>close</Icon>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Botão de upload */}
            {files.length < 5 && (
              <button
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 text-xs"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#38bdf8', padding: 0,
                }}
              >
                <Icon style={{ fontSize: 18 }}>attach_file</Icon>
                {files.length === 0 ? 'Adicionar arquivo ou foto' : `Adicionar mais (${files.length}/5)`}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
              style={{ display: 'none' }}
              onChange={handleFiles}
            />
            <p className="mt-2 text-xs" style={{ color: '#475569' }}>
              Imagens, PDF, Word ou TXT — máx. 8 MB por arquivo, até 5 arquivos
            </p>
          </div>
        </Field>

        {/* Rodapé */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => history.push('/admin/tickets')}
            className="text-sm"
            style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Cancelar
          </button>
          <Button
            textcolor="#fff"
            padding="8px 24px"
            margintop="0px"
            width="180"
            onClick={handleSubmit}
            customcolor={props.theme.color}
            disabled={submitting}
          >
            {submitting
              ? <CircularProgress size={16} style={{ color: '#fff' }} />
              : <><Icon className="mr-2" style={{ fontSize: 16 }}>confirmation_number</Icon>Abrir ticket</>
            }
          </Button>
        </div>
      </div>
    </>
  );
};

const inputStyle = {
  background: '#0a1929',
  border: '1px solid #1e3a5f',
  color: '#e2e8f0',
  outline: 'none',
};

const Field = ({ label, error, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium mb-2" style={{ color: '#94a3b8' }}>
      {label}
    </label>
    {children}
    {error && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{error}</p>}
  </div>
);

export default Create;
